import React, { useCallback, useMemo } from 'react';
import { translate } from '@shakkerai/util-i18n';
import { GenerateButton } from '@shakkerai/pro-components';
// import { outpaintPresetModels } from '@shakkerai/pro-models';
import { notifyWarn } from '@shakkerai/pro-components';
import { updateUnitData, getUnitData } from '@starflow/app/helpers/flow';
import { GeneratorPanelFooter } from '@starflow/app/modules/generator/components/_legacy';
import { Prompt } from '@starflow/app/modules/generator/components/_legacy';
import { scheduleTaskForUnit } from '@starflow/app/helpers/process';
import { useDerivativeTasks } from '@starflow/app/modules/derivative';
import OutpaintGtnGroup from '../OutpaintBtnGroup';
import { SizeInputGroup } from '../SizeInputGroup';
import { MIN_GENERATOR_SIZE, MAX_GENERATOR_SIZE } from '@shakkerai/pro-constants';
import { getViewport } from '@starflow/app/store';
import { resizeInnerId, resizeWrapperId } from '../../constants';
import { useProjectState } from '@starflow/app/store';
import { domRectToObject } from '../../utils';

const Preparing = () => {
  return <div />;
};

// const ModelPreset = React.memo(({ value, disabled, allowUseOriginal, onChange }) => {
//   return (
//     <div className="grid grid-cols-3 gap-1">
//       {outpaintPresetModels.map((item) => {
//         if (item.key === 'original' && !allowUseOriginal) {
//           return null;
//         }

//         const selected = item.key === value;

//         return (
//           <button
//             key={item.key}
//             disabled={selected}
//             onClick={() => onChange(item)}
//             className={`${disabled ? 'opacity-60 cursor-not-allowed' : ''} ${
//               selected ? 'bg-primary/10 border-primary text-[#005848]' : 'bg-[#F4F6F8] border-white'
//             } text-xs text-center h-7 rounded-sm text-[#2D2D2D] border`}
//           >
//             {item.label}
//           </button>
//         );
//       })}
//     </div>
//   );
// });

export const OutpaintPanel = React.memo(({ element, preparing, generator }) => {
  const outPaintResultSelectedIndex = useProjectState('outPaintResultSelectedIndex');
  const isDisableResize = outPaintResultSelectedIndex >= 0;
  const unitId = element.lastDerivativeUnitId;
  const tasks = useDerivativeTasks(unitId);
  const data = getUnitData(generator.id);
  // const selectedItem = tasks[selectedIndex];
  const isPending = tasks?.status === 'pending';

  const handleGenerate = useCallback(() => {
    scheduleTaskForUnit(generator.id);
  }, [generator.id]);

  // const handleSwitchModelPreset = useCallback(
  //   (obj) => {
  //     updateUnitData(generator.id, { checkpointId: obj.key });
  //   },
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  //   [generator.id],
  // );

  if (preparing) {
    return (
      <div id="outpaint-generator-panel" className="py-4 px-3 flex-1 overflow-auto tiny-scrollbar">
        <Preparing preparing={preparing} imageId={element.id} />
      </div>
    );
  }

  const handleSize = useCallback(
    (rate) => {
      const { width, height } = element;
      let lastW = width;
      let lastH = height;
      if (rate === '1.00') {
        let maxSide = Math.max(width, height);
        if (maxSide > MAX_GENERATOR_SIZE) {
          maxSide = MAX_GENERATOR_SIZE;
          // 图片过大
          notifyWarn(translate('outpaint_over_size_tips'));
        }
        lastW = lastH = maxSide;
      } else {
        // 宽高比<1
        if (width <= height) {
          lastW = width;
          lastH = width / rate;
          if (lastH < height) {
            // 图片过高
            lastH = height;
            notifyWarn(translate('outpaint_over_size_tips'));
          }
        } else {
          lastH = height;
          lastW = height * rate;
          if (lastW < width) {
            // 图片过宽
            notifyWarn(translate('outpaint_over_size_tips'));
            lastW = width;
          }
        }
      }
      const { scale } = getViewport();
      const wrapperDom = document.getElementById(resizeWrapperId);
      const innerDom = document.getElementById(resizeInnerId);
      const wrapper = domRectToObject(wrapperDom.getBoundingClientRect());
      const inner = domRectToObject(innerDom.getBoundingClientRect());
      const innerObj = {
        ...inner,
        width: inner.width / scale,
        height: inner.height / scale,
      };
      const wrapperObj = {
        ...wrapper,
        width: lastW,
        height: lastH,
      };
      const params = {
        outpaintRect: {
          wrapper: wrapperObj,
          inner: innerObj,
          relativeXY: {
            left: innerObj.x - wrapperObj.x,
            right: (wrapperObj.x + wrapperObj.width * scale - innerObj.x - innerObj.width * scale) / scale,
            top: innerObj.y - wrapperObj.y,
            bottom: (wrapperObj.y + wrapperObj.height * scale - innerObj.y - innerObj.height * scale) / scale,
          },
        },
      };
      updateUnitData(generator.id, params);
    },
    [element],
  );

  const handleChangeSize = useCallback(
    (value = {}) => {
      const { scale } = getViewport();
      const wrapperDom = document.getElementById(resizeWrapperId);
      const innerDom = document.getElementById(resizeInnerId);
      const wrapperObj = domRectToObject(wrapperDom.getBoundingClientRect());
      const inner = domRectToObject(innerDom.getBoundingClientRect());
      if (value.width) {
        wrapperObj.width = value.width < element.width ? element.width : value.width;
        wrapperObj.height = wrapperObj.height / scale;
      }

      if (value.height) {
        wrapperObj.height = value.height < element.height ? element.height : value.height;
        wrapperObj.width = wrapperObj.width / scale;
      }

      const innerObj = {
        ...inner,
        width: inner.width / scale,
        height: inner.height / scale,
      };
      const params = {
        outpaintRect: {
          wrapper: wrapperObj,
          inner: innerObj,
          relativeXY: {
            left: innerObj.x - wrapperObj.x,
            right: (wrapperObj.x + wrapperObj.width * scale - innerObj.x - innerObj.width * scale) / scale,
            top: innerObj.y - wrapperObj.y,
            bottom: (wrapperObj.y + wrapperObj.height * scale - innerObj.y - innerObj.height * scale) / scale,
          },
        },
      };
      updateUnitData(generator.id, params);
    },
    [generator.id, element],
  );

  const sizeValue = useMemo(() => {
    if (data.outpaintRect) {
      const { width, height } = data.outpaintRect.wrapper;
      return {
        width: Math.round(width),
        height: Math.round(height),
        lockRatio: false,
      };
    } else {
      return {
        width: Math.round(element.width),
        height: Math.round(element.height),
        lockRatio: false,
      };
    }
  }, [data, element]);

  // const checkpointId = data?.checkpointId ?? outpaintPresetModels[0].key;

  const notAllowedTip = useMemo(() => {
    const width = Math.round(element.width * element.scaleX);
    const height = Math.round(element.height * element.scaleY);
    // if (!generator?.data?.checkpointId) {
    //   return translate('imagetool_inpaint_choose_style_tip');
    // }

    if (width < MIN_GENERATOR_SIZE || height < MIN_GENERATOR_SIZE) {
      return translate('imagetool_outpaint_size_minlimit');
    }

    if (!generator?.data?.outpaintRect) {
      return translate('imagetool_outpaint_size_adjust');
    }

    if (sizeValue.width < width + 8 && sizeValue.height < height + 8) {
      return translate('imagetool_outpaint_exetends_minlimit');
    }

    return null;
  }, [sizeValue, generator, element.width, element.height, element.scaleX, element.scaleY]);
  return (
    <>
      <h2 className="flex items-center px-4 h-[44px] border-b border-[#efefef] font-bold text-sm text-[#2d2d2d] font-grotesk">
        {translate('imagetool_outpaint')}
      </h2>
      <div id="outpaint-generator-panel" className="pt-4 px-4 pb-1 flex-1 overflow-auto tiny-scrollbar">
        <h5 className="flex-1 text-[#2d2d2d] text-xs leading-[14px] font-bold font-gro">
          {translate('imagetool_outpaint_size')}
        </h5>
        <OutpaintGtnGroup onChange={(e) => handleSize(e)} image={data} isDisableResize={isDisableResize} />
        <div className="flex items-center justify-between mt-3">
          <span className="flex-1 text-[#7F7F7F] text-xs">{translate('original_setting_size')}</span>
          <span className="text-xs text-[#2d2d2d] mr-2">{`${Math.round(element.width)} * ${Math.round(
            element.height,
          )}`}</span>
        </div>
        <div className="flex items-center justify-between mt-1">
          <span className="flex-1 text-[#7F7F7F] text-xs">{translate('imgref_similarity_cus')}</span>
          <SizeInputGroup hideLockButton value={sizeValue} onChange={handleChangeSize} disabled={isDisableResize} />
        </div>
        {/* <div className="-mx-4 my-3.5 border-t border-[#efefef]" />
        <h5 className="flex-1 mb-[12px] text-[#2d2d2d] text-xs leading-[14px] font-bold font-gro">
          {translate('imagetool_inpiant_style')}
        </h5>
        <ModelPreset value={checkpointId} onChange={handleSwitchModelPreset} /> */}
        <div className="-mx-4 my-3.5 border-t border-[#efefef]" />
        <Prompt isFull={false} generator={generator} />
        <div className="mb-32" />
      </div>
      <GeneratorPanelFooter generator={generator}>
        <GenerateButton disabled={!!notAllowedTip || isPending} tooltip={notAllowedTip} onClick={handleGenerate}>
          {translate(isPending ? 'generator_pending' : 'imagetool_outpaint')}
        </GenerateButton>
      </GeneratorPanelFooter>
    </>
  );
});
